import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "~/components/ui/dialog"
import { Button } from "~/components/ui/button"
import { AlertTriangle, ExternalLink } from "lucide-react"

/** Deadline: current editor stops being available — May 15, 2026 */
const MIGRATION_DEADLINE = new Date("2026-05-15T00:00:00.000Z")

/** Link to the new integrated editor */
const NEW_EDITOR_URL = "https://selfpublishingtitans.com"

function getCountdown(): { days: number; hours: number; minutes: number; seconds: number } {
  const now = new Date()
  const diff = MIGRATION_DEADLINE.getTime() - now.getTime()
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 }
  }
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)
  return { days, hours, minutes, seconds }
}

interface EditorMigrationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function EditorMigrationDialog({ open, onOpenChange }: EditorMigrationDialogProps) {
  const [countdown, setCountdown] = useState(getCountdown())

  useEffect(() => {
    if (!open) return
    const interval = setInterval(() => setCountdown(getCountdown()), 1000)
    return () => clearInterval(interval)
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-visible">
        <DialogHeader>
          <div className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-6 w-6 shrink-0" />
            <DialogTitle className="text-xl font-bold text-gray-900">
              Important: New integrated editor available
            </DialogTitle>
          </div>
          <DialogDescription asChild>
            <div className="space-y-4 pt-2 text-left text-sm text-gray-600">
              <p>
                We have built a <strong>new integrated editor</strong> with a better experience and more tools. Please
                use it here:{" "}
                <a
                  href={NEW_EDITOR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-semibold text-indigo-600 underline hover:text-indigo-700"
                >
                  Open new editor <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </p>
              <p>
                The new editor includes <strong>all current features plus new advanced features</strong>. We recommend
                moving your work there.
              </p>
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
                <strong>This current editor will stop being available in 60 days.</strong> Please{" "}
                <strong>download your work</strong> before the countdown below expires.
              </p>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Time remaining (until May 15, 2026)
                </p>
                <div className="flex flex-wrap justify-center gap-3 text-center">
                  <div>
                    <span className="block text-2xl font-bold tabular-nums text-gray-900">
                      {String(countdown.days).padStart(2, "0")}
                    </span>
                    <span className="text-xs text-gray-500">Days</span>
                  </div>
                  <div>
                    <span className="block text-2xl font-bold tabular-nums text-gray-900">
                      {String(countdown.hours).padStart(2, "0")}
                    </span>
                    <span className="text-xs text-gray-500">Hours</span>
                  </div>
                  <div>
                    <span className="block text-2xl font-bold tabular-nums text-gray-900">
                      {String(countdown.minutes).padStart(2, "0")}
                    </span>
                    <span className="text-xs text-gray-500">Min</span>
                  </div>
                  <div>
                    <span className="block text-2xl font-bold tabular-nums text-gray-900">
                      {String(countdown.seconds).padStart(2, "0")}
                    </span>
                    <span className="text-xs text-gray-500">Sec</span>
                  </div>
                </div>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row">
          <Button variant="default" className="w-full sm:w-auto" onClick={() => window.open(NEW_EDITOR_URL, "_blank")}>
            <ExternalLink className="h-4 w-4" />
            Go to new editor
          </Button>
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>
            I understand, continue here
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
